import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import {
  Building2,
  Plus,
  Trash2,
  ChevronLeft,
  UserPlus,
  Lock,
  ShieldCheck,
  Save,
  Power,
  Search,
  Handshake,
} from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import PermissionCheckboxGroup from './PermissionCheckboxGroup';
import PlatformCompanyKeys from './PlatformCompanyKeys';
import { normalizeCredentials } from '../shared/LLMKeys/llmKeyStatus';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { EMPTY_PARTNER_INDEX, isHomeCompany } from '../../utils/tenancy';
import { describeApiError, mapApiError } from '../../utils/apiError';
import { PLATFORM_PARTNER_COPY } from '../../constants/partnerCopy';
import PaginationBar from './PaginationBar';
import { platformMcpTooltips, platformMcpUntickWarning } from '../../utils/platformMcpPermission';
import styles from './PlatformAdmin.module.scss';

const ENT_KEY_TOOLTIPS = platformMcpTooltips('companyLimits');

const PAGE_SIZE = 50;

// A partner's home company is (de)activated only with its partner (§4.7).
const HOME_COMPANY_ERRORS = [
  { status: 400, match: /home company/i, message: PLATFORM_PARTNER_COPY.homeCompanyStatus },
];

/**
 * Companies panel (spec §4.1): companies list + detail with the LLM-key matrix,
 * user assignment, deactivation, and the entitlements (allowed-areas) editor.
 *
 * Partner tier (§4.7): a Partner column; partner home companies are hidden (their
 * staff are managed from Partners → Admins & users); a partner's client company
 * shows its keys through the partner chain with an inheritance toggle.
 */
const CompaniesPanel = ({ notify, catalog, partnerIndex = EMPTY_PARTNER_INDEX }) => {
  const companyPerms = useMemo(
    () => (catalog || []).filter((p) => p.scope === 'company'),
    [catalog]
  );

  const [providers, setProviders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState('providers');

  const [createOpen, setCreateOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creating, setCreating] = useState(false);

  // The owning partner's own key rows — needed to resolve a client company's
  // effective key (company → partner → platform).
  const [partnerCreds, setPartnerCreds] = useState([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [activating, setActivating] = useState(false);

  // Entitlements editor state
  const [entKeys, setEntKeys] = useState([]);
  // What the server holds, so unticking a saved key can warn before saving.
  const [entSavedKeys, setEntSavedKeys] = useState([]);
  const [entLoading, setEntLoading] = useState(false);
  const [entSaving, setEntSaving] = useState(false);
  const [entConfirmOpen, setEntConfirmOpen] = useState(false);

  // Providers feed the company-detail key matrix; load once (getProviders already
  // asks for a big page).
  const loadProviders = useCallback(async () => {
    try {
      const providerData = await platformAdminService.getProviders(true);
      setProviders(Array.isArray(providerData) ? providerData : providerData?.providers || []);
    } catch (err) {
      notify(err?.message || 'Failed to load providers.', 'error');
    }
  }, [notify]);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listCompanies(false, {
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setCompanies(data?.companies || (Array.isArray(data) ? data : []));
      setTotal(Number.isFinite(data?.total) ? data.total : (data?.companies || []).length);
    } catch (err) {
      setError(err?.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, offset]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const openCompany = useCallback(
    async (companyId) => {
      try {
        setDetailLoading(true);
        setTab('providers');
        const data = await platformAdminService.getCompany(companyId);
        setDetail(data);
      } catch (err) {
        notify(err?.message || 'Failed to load company.', 'error');
      } finally {
        setDetailLoading(false);
      }
    },
    [notify]
  );

  const refreshDetail = useCallback(async () => {
    if (!detail?.id) return;
    try {
      const data = await platformAdminService.getCompany(detail.id);
      setDetail(data);
    } catch (err) {
      notify(err?.message || 'Failed to refresh company.', 'error');
    }
  }, [detail?.id, notify]);

  // Reactivate a soft-deactivated company (there's no dedicated endpoint — flip
  // is_active back via PUT). Name is included in case the backend PUT is a full
  // replacement.
  const handleActivateCompany = async () => {
    if (!detail?.id) return;
    try {
      setActivating(true);
      await platformAdminService.updateCompany(detail.id, { name: detail.name, is_active: true });
      notify('Company activated. Its users can log in again.', 'success');
      await refreshDetail();
      await loadCompanies();
    } catch (err) {
      notify(mapApiError(err, HOME_COMPANY_ERRORS, 'Failed to activate company.'), 'error');
    } finally {
      setActivating(false);
    }
  };

  const detailPartner = detail?.partner_id != null ? partnerIndex.byId.get(Number(detail.partner_id)) : null;
  const partnerHomeId = detailPartner?.home_company_id ?? null;

  const loadPartnerCreds = useCallback(async () => {
    if (partnerHomeId == null) {
      setPartnerCreds([]);
      return;
    }
    try {
      const data = await platformAdminService.getCompanyLLMKeys(partnerHomeId);
      setPartnerCreds(normalizeCredentials(data));
    } catch (err) {
      setPartnerCreds([]);
      notify(describeApiError(err, "Couldn't load the partner's keys."), 'error');
    }
  }, [partnerHomeId, notify]);

  useEffect(() => {
    loadPartnerCreds();
  }, [loadPartnerCreds]);

  // Load entitlements whenever a company detail opens.
  useEffect(() => {
    if (!detail?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setEntLoading(true);
        const data = await platformAdminService.getEntitlements(detail.id);
        if (!cancelled) {
          const keys = Array.isArray(data?.permission_keys) ? data.permission_keys : [];
          setEntKeys(keys);
          setEntSavedKeys(keys);
        }
      } catch (err) {
        if (!cancelled) notify(err?.message || 'Failed to load entitlements.', 'error');
      } finally {
        if (!cancelled) setEntLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.id, notify]);

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      setCreating(true);
      await platformAdminService.createCompany({ name: newCompanyName.trim() });
      notify('Company created.', 'success');
      setCreateOpen(false);
      setNewCompanyName('');
      await loadCompanies();
    } catch (err) {
      notify(err?.message || 'Failed to create company.', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Home companies are managed from Partners (their staff = "Direct users"), so
  // they never show here as companies (§4.7).
  const visibleCompanies = useMemo(
    () => companies.filter((c) => !isHomeCompany(c.id, { partnerIndex, name: c.name })),
    [companies, partnerIndex]
  );

  const handleAssignUser = async () => {
    if (!assignEmail.trim()) return;
    try {
      setAssigning(true);
      await platformAdminService.assignCompanyUser(detail.id, { email: assignEmail.trim() });
      notify('User assigned.', 'success');
      setAssignOpen(false);
      setAssignEmail('');
      await refreshDetail();
    } catch (err) {
      notify(err?.message || 'Failed to assign user.', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      setConfirmBusy(true);
      if (confirm.type === 'removeUser') {
        await platformAdminService.unassignCompanyUser(detail.id, confirm.userId);
        notify('User removed.', 'info');
        await refreshDetail();
      } else if (confirm.type === 'deleteCompany') {
        await platformAdminService.deleteCompany(confirm.companyId);
        notify('Company deactivated.', 'info');
        setDetail(null);
        await loadCompanies();
      }
      setConfirm(null);
    } catch (err) {
      notify(mapApiError(err, HOME_COMPANY_ERRORS, 'Action failed.'), 'error');
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Entitlements ──
  const entSelectedSet = useMemo(() => new Set(entKeys), [entKeys]);
  const toggleEnt = (key) => {
    setEntKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const removedKeys = useMemo(() => {
    // Compared against the catalog: which company areas are currently unchecked.
    return companyPerms.filter((p) => !entSelectedSet.has(p.key)).map((p) => p.key);
  }, [companyPerms, entSelectedSet]);

  const handleSaveEntitlements = async () => {
    try {
      setEntSaving(true);
      const data = await platformAdminService.setEntitlements(detail.id, entKeys);
      const saved = Array.isArray(data?.permission_keys) ? data.permission_keys : entKeys;
      setEntKeys(saved);
      setEntSavedKeys(saved);
      notify('Entitlements updated.', 'success');
      setEntConfirmOpen(false);
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to save entitlements.', 'error');
    } finally {
      setEntSaving(false);
    }
  };

  return (
    <>
      {!detail && (
        <Box className={styles.toolbar}>
          <TextField
            size="small"
            placeholder="Search companies…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setCreateOpen(true)}
            className={styles.primaryBtn}
          >
            New company
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Company list ── */}
      {!detail &&
        (loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : visibleCompanies.length === 0 ? (
          <Box className={styles.emptyState}>
            <Building2 className={styles.emptyIcon} size={56} />
            <Typography variant="h6">No companies yet</Typography>
            <Typography variant="body2">Create your first company to start onboarding.</Typography>
          </Box>
        ) : (
          <Box className={styles.tableCard}>
            {visibleCompanies.map((company) => {
              const partner =
                company.partner_id != null ? partnerIndex.byId.get(Number(company.partner_id)) : null;
              return (
                <Box
                  key={company.id}
                  className={styles.companyRow}
                  onClick={() => openCompany(company.id)}
                  role="button"
                  tabIndex={0}
                >
                  <Box className={styles.companyMain}>
                    <span className={styles.companyAvatar}>
                      {(company.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <Box>
                      <span className={styles.companyName}>{company.name}</span>
                      <span className={styles.companyMeta}>ID {company.id}</span>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <span className={styles.badge} title="Partner">
                      <Handshake size={12} />
                      {company.partner_id == null
                        ? '—'
                        : partner?.name || `Partner #${company.partner_id}`}
                    </span>
                    <span className={`${styles.statusPill} ${company.is_active ? styles.active : styles.inactive}`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

      {!detail && total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="companies"
          onOffsetChange={setOffset}
        />
      )}

      {/* ── Company detail ── */}
      {detail && (
        <>
          <Box className={styles.detailHeader}>
            <Button
              onClick={() => setDetail(null)}
              startIcon={<ChevronLeft size={18} />}
              className={styles.backBtn}
            >
              All companies
            </Button>
            <Box className={styles.detailTitleRow}>
              <Typography variant="h5" className={styles.detailTitle}>{detail.name}</Typography>
              <span className={`${styles.statusPill} ${detail.is_active ? styles.active : styles.inactive}`}>
                {detail.is_active ? 'Active' : 'Inactive'}
              </span>
              {detail.partner_id != null && (
                <span className={styles.badge}>
                  <Handshake size={12} />
                  {detailPartner?.name || `Partner #${detail.partner_id}`}
                </span>
              )}
              {detail.is_active ? (
                <IconButton
                  size="small"
                  className={styles.dangerIcon}
                  title="Deactivate company"
                  onClick={() =>
                    setConfirm({
                      type: 'deleteCompany',
                      companyId: detail.id,
                      heading: `Deactivate ${detail.name}?`,
                      text: 'This soft-deletes the company and immediately locks out every user in it — they will be signed out and unable to log in until the company is reactivated. Keys and data are retained.',
                      action: 'Deactivate',
                    })
                  }
                >
                  <Trash2 size={16} />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  className={styles.subtleBtn}
                  title="Activate company"
                  disabled={activating}
                  onClick={handleActivateCompany}
                >
                  {activating ? <CircularProgress size={16} color="inherit" /> : <Power size={16} />}
                </IconButton>
              )}
            </Box>
          </Box>

          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                className={styles.tabs}
                TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
              >
                <Tab value="providers" label="LLM Providers" className={styles.tab} />
                <Tab value="users" label={`Users (${detail.users?.length || 0})`} className={styles.tab} />
                <Tab value="entitlements" label="Entitlements" className={styles.tab} />
              </Tabs>

              {tab === 'providers' && (
                <PlatformCompanyKeys
                  companyId={detail.id}
                  companyName={detail.name}
                  variant={detail.partner_id != null ? 'client' : 'direct'}
                  partnerName={detailPartner?.name}
                  providers={providers}
                  credentials={detail.llm_credentials || []}
                  partnerCredentials={partnerCreds}
                  onChanged={refreshDetail}
                  notify={notify}
                />
              )}

              {tab === 'users' && (
                <Box className={styles.tableCard}>
                  <Box className={styles.usersToolbar}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Attach an existing user to this company by email.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UserPlus size={16} />}
                      onClick={() => setAssignOpen(true)}
                      className={styles.primaryBtn}
                    >
                      Assign user
                    </Button>
                  </Box>
                  {(detail.users || []).length === 0 ? (
                    <Box className={styles.usersEmpty}>No users assigned yet.</Box>
                  ) : (
                    (detail.users || []).map((user) => (
                      <Box key={user.id} className={styles.userRow}>
                        <Box className={styles.companyMain}>
                          <span className={styles.companyAvatar}>
                            {(user.first_name || user.email || '?').charAt(0).toUpperCase()}
                          </span>
                          <Box>
                            <span className={styles.companyName}>
                              {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
                            </span>
                            <span className={styles.companyMeta}>{user.email}</span>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          className={styles.dangerIcon}
                          title="Remove user"
                          onClick={() =>
                            setConfirm({
                              type: 'removeUser',
                              userId: user.id,
                              heading: `Remove ${user.email}?`,
                              text: 'Unassigns this user from the company.',
                              action: 'Remove',
                            })
                          }
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    ))
                  )}
                </Box>
              )}

              {tab === 'entitlements' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box className={styles.noteBanner}>
                    <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Entitlements are the areas this company is allowed to use at all. A role can grant
                      an area, but if it is unchecked here, every user in the company is blocked from it.
                      New companies start with all areas enabled except reading run content.
                    </span>
                  </Box>
                  {detail.partner_id != null && (
                    <Box className={styles.noteBanner}>
                      <Handshake size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        {`This company belongs to ${detailPartner?.name || 'a partner'}. What its users can actually do is also capped by the partner's limits — anything off there stays off here. The partner manages these limits day to day; only the platform can turn on reading run content (usage_run_content), and it only takes effect if it's also on for the partner.`}
                      </span>
                    </Box>
                  )}

                  {entLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                      <CircularProgress color="primary" />
                    </Box>
                  ) : (
                    <>
                      <PermissionCheckboxGroup
                        catalog={companyPerms}
                        selected={entKeys}
                        onToggle={toggleEnt}
                        keyTooltips={ENT_KEY_TOOLTIPS}
                        keyWarnings={platformMcpUntickWarning(entSavedKeys, entKeys)}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                          {entKeys.length} of {companyPerms.length} areas enabled
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Save size={16} />}
                          className={styles.primaryBtn}
                          onClick={() => setEntConfirmOpen(true)}
                          disabled={entSaving}
                        >
                          Save entitlements
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Create company dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>New company</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="Company name"
            fullWidth
            variant="outlined"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            className={styles.formField}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompany} disabled={creating || !newCompanyName.trim()} className={styles.primaryBtn}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign user */}
      <Dialog open={assignOpen} onClose={() => !assigning && setAssignOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Assign user</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A user from another company is moved here and loses their company admin role there.
          </Typography>
          <TextField
            autoFocus
            label="User email"
            type="email"
            fullWidth
            variant="outlined"
            placeholder="user@company.com"
            value={assignEmail}
            onChange={(e) => setAssignEmail(e.target.value)}
            className={styles.formField}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setAssignOpen(false)} disabled={assigning} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignUser} disabled={assigning || !assignEmail.trim()} className={styles.primaryBtn}>
            {assigning ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Entitlements save confirmation */}
      <Dialog open={entConfirmOpen} onClose={() => !entSaving && setEntConfirmOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Update entitlements</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>
            Apply {entKeys.length} enabled / {removedKeys.length} disabled areas?
          </Typography>
          <Typography className={styles.deleteText}>
            Disabling an area restricts every user in {detail?.name} from it immediately — no role edits
            needed. Re-enabling restores access.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setEntConfirmOpen(false)} disabled={entSaving} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEntitlements} disabled={entSaving} className={styles.primaryBtn}>
            {entSaving ? <CircularProgress size={20} color="inherit" /> : 'Apply changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic confirmation */}
      <Dialog open={!!confirm} onClose={() => !confirmBusy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{confirm?.action}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm?.heading}</Typography>
          <Typography className={styles.deleteText}>{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runConfirm}
            disabled={confirmBusy}
            className={styles.dangerButton}
            startIcon={confirmBusy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirmBusy ? 'Working...' : confirm?.action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompaniesPanel;
